
import { apiService } from "@services/api.service";
import CancelToken, { AxiosResponse, CancelTokenSource } from "axios";
import { CreateToast } from "@utils/toasts";
import { BaseView } from "@components/base/view";
import { Component, Watch } from "vue-property-decorator";
import { NavigationGuardNext, Route } from "vue-router";
import { Loader, useLoader } from "@composables/loader";
import { ciTrackingService } from "@services/ci-tracking.service";
import InterestSelectionIntroduction from "@images/interest-selection-introduction.svg";
import { HandleRouterError } from "@router/error-handler";
import { ADS_INVENTORY } from "@models/constants/ads_inventory";
import { ROUTENAMES } from "@models/constants/routes";
import { MetaInfo } from "vue-meta";
import CiButton from "@components/base/button.vue";
import CiCheckbox from "@components/base/forms/checkbox.vue";
import CiContentTile from "@components/contents/ContentTile.vue";
import CiRegistrationModal from "@components/base/registration-modal.vue";
import CiSuggestedExternalContent from "@components/ads/suggested-external-content.vue";
import { alternateService } from "@services/alternate.service";
import { settingsService } from "@services/settings.service";

@Component({
  name: "ci-newscenter-index",
  components: {
    "ci-button": CiButton,
    "ci-checkbox": CiCheckbox,
    "ci-content-tile": CiContentTile,
    "ci-registration-modal": CiRegistrationModal,
    "ci-suggested-external-content": CiSuggestedExternalContent,
  },
  metaInfo(): MetaInfo {
    const title = this.$t("ui.views.news_center.meta_tags.title") as string;
    const description = this.$t("ui.views.news_center.meta_tags.description") as string;
    return {
      title: title,
      link: alternateService.fromRoute(this.$route),
      meta: [
        { vmid: "description", name: "description", content: description },
        { vmid: "og:title", property: "og:title", content: title },
        { vmid: "og:description", property: "og:description", content: description },
        { vmid: "twitter:title", name: "twitter:title", content: title },
        { vmid: "twitter:description", name: "twitter:description", content: description },
      ],
    };
  },
})
export default class extends BaseView {
  TABS = {
    MOST_RELEVANT: "most_relevant",
    LATEST: "latest",
    MOST_READ: "most_read",
  };

  public articles: Articles.Article[] = [];
  public error: boolean = false;
  public loadingBtnState: boolean = false;

  public filter = 0;
  public sort_by = 0;

  public filterByTags: boolean = false;

  public page: number = 1;
  public meta: NewsCenterApi.Meta = {
    current_page: 0,
    next_page: 0,
    prev_page: 0,
    total_count: 0,
    total_pages: 0,
  };

  public filterByWithin = {
    current: "oneweek",
    options: [
      {
        value: "oneweek",
        label: this.$t("ui.views.news_center.within.oneweek") as string,
        id: "oneweek",
      },
      {
        value: "onemonth",
        label: this.$t("ui.views.news_center.within.onemonth") as string,
        id: "onemonth",
      },
      {
        value: "oneyear",
        label: this.$t("ui.views.news_center.within.oneyear") as string,
        id: "oneyear",
      },
    ],
  };

  public tabs = {
    current: this.initialTab(),
    options: [
      {
        value: this.TABS.MOST_RELEVANT,
        label: this.$t(`ui.views.news_center.tabs.${this.TABS.MOST_RELEVANT}`) as string,
        icon: "mdi-star-outline",
        id: this.TABS.MOST_RELEVANT,
      },
      {
        value: this.TABS.LATEST,
        label: this.$t(`ui.views.news_center.tabs.${this.TABS.LATEST}`) as string,
        icon: "mdi-timer-outline",
        id: this.TABS.LATEST,
      },
      {
        value: this.TABS.MOST_READ,
        label: this.$t(`ui.views.news_center.tabs.${this.TABS.MOST_READ}`) as string,
        icon: "mdi-trending-up",
        id: this.TABS.MOST_READ,
      },
    ],
  };

  private cancelToken: CancelTokenSource | null = null;
  private loader = useLoader();

  get showNewsSourcesEdit(): boolean {
    return settingsService.hasFlipper("news_sources");
  }

  public get interestSelectionIntroduction(): ImportedSVG {
    return InterestSelectionIntroduction;
  }

  public get adIds(): { header: string; skyscraper: string; sidebar_2nd: string } {
    return {
      header: ADS_INVENTORY.news_center_header_slot.dom_id,
      skyscraper: ADS_INVENTORY.news_center_sidebar_top.dom_id,
      sidebar_2nd: ADS_INVENTORY.news_center_sidebar_2nd.dom_id,
    };
  }

  public get hasArticles(): boolean {
    return this.articles.length > 0;
  }

  public get isLoading(): boolean {
    return Loader.loading.state;
  }

  public get showInterestPrompt(): boolean {
    return (
      (this.tabs.current === this.TABS.MOST_RELEVANT ||
        (this.tabs.current === this.TABS.LATEST && this.filterByTags)) &&
      this.meta.user_has_interest_tags === false
    );
  }

  @Watch("filter")
  onTabFilterChanged(val: number): void {
    this.tabs.current = this.tabs.options[val].value;
    this.resetAndFetchAgain();

    this.applyRouteForCurrentFilters(this.tabs.current);
  }

  @Watch("sort_by")
  async onTabSortByChanged(clickedTabIndex: number): Promise<void> {
    if (this.$user.signedIn) {
      this.filterByWithin.current = this.filterByWithin.options[clickedTabIndex].value;
      this.resetAndFetchAgain();
      this.applyRouteForCurrentFilters(this.tabs.current);
    } else {
      await this.$nextTick();
      this.sort_by = 0;
      this.openRegistrationModal();
    }
  }

  @Watch("filterByTags")
  public handleChangeInterestBasedFilter(): void {
    if (this.$user.signedIn) {
      this.handleInterestBasedFilterParamChange();
      this.resetAndFetchAgain();
    } else {
      this.openRegistrationModal();
      // you can't update the value you are watching in the watcher so we have to update it later.
      this.$nextTick(() => (this.filterByTags = false));
    }
  }

  public handleEditNewsSourcesClick(): void {
    if (!this.$user.signedIn) {
      this.openRegistrationModal();
    } else {
      this.$router.push({ name: ROUTENAMES.USERS_SETTINGS, query: { tab: "news_sources" } });
    }
    this.trackUserInterestClick("UserNewsSources");
  }

  public handleEditInterests(): void {
    if (!this.$user.signedIn) {
      this.openRegistrationModal();
    } else {
      this.$router.push({ name: ROUTENAMES.USERS_SETTINGS, query: { tab: "my_interests" } });
    }
    this.trackUserInterestClick("UserInterests");
  }

  public openRegistrationModal(): void {
    this.$bvModal.show("modal-registration-soft-gating");
  }

  public async beforeRouteEnter(to: Route, _from: Route, next: NavigationGuardNext): Promise<void> {
    next((vm: any) => {
      useLoader().start();
      vm.initialize();
    });
  }

  public fetchNextPage(): void {
    this.page++;
    this.fetchArticles();
  }

  protected mounted(): void {
    try {
      this.applyFiltersFromUrlCalledByMounted();
      // this will trigger a view event, don't explicitly track again
      this.applyRouteForCurrentFilters(this.tabs.current, true);

      if (this.$route.query.currentTab === undefined || this.$route.query.currentTab === "most_relevant") {
        this.resetAndFetchAgain();
      }
    } catch (err) {
      apiService.handleAPIError(err);
    } finally {
      this.loader.stop();
    }
  }

  private handleInterestBasedFilterParamChange(): void {
    if (this.$route.query.interest_based !== this.filterByTags.toString()) {
      this.$router.push({ query: { currentTab: this.tabs.current, interest_based: this.filterByTags.toString() } });

      this.trackNewscenterActions(this.tabs.options.findIndex((obj) => obj.value === this.tabs.current));
    }
  }

  private applyFiltersFromUrlCalledByMounted(): void {
    const currentTab = (this.$route.query.currentTab as string) || this.initialTab();
    let currentSortBy = "oneweek";
    let currentFilterByTags = false;

    if (this.$user.signedIn) {
      currentSortBy = (this.$route.query.sort_by as string) || "oneweek";
      currentFilterByTags = this.$route.query.interest_based === "true";
    }

    this.tabs.current = currentTab;
    this.filterByWithin.current = currentSortBy;
    this.filterByTags = currentFilterByTags;
  }

  private applyRouteForCurrentFilters(currentTab: string, keepTrackingParameters: boolean = false): void {
    // keep non-filter parameters (e.g. utm tracking)
    const filterParameters = ["currentTab", "interest_based", "sort_by"];
    let baseParameters = {};
    if (keepTrackingParameters) {
      baseParameters = Object.keys(this.$router.currentRoute.query)
        .filter((key) => !filterParameters.includes(key))
        .reduce(
          (obj, key) => ({
            ...obj,
            [key]: this.$router.currentRoute.query[key],
          }),
          {}
        );
    }

    let newQuery;
    switch (currentTab) {
      case this.TABS.MOST_RELEVANT:
        newQuery = { ...baseParameters, currentTab };
        break;
      case this.TABS.LATEST:
        newQuery = { ...baseParameters, currentTab, interest_based: this.filterByTags.toString() };
        break;
      case this.TABS.MOST_READ:
        newQuery = { ...baseParameters, currentTab, sort_by: this.filterByWithin.current };
        break;
      default:
        newQuery = { ...baseParameters, currentTab };
        break;
    }

    this.replaceCurrentRoute(newQuery);
  }

  private initialTab(): string {
    if (this.$user.signedIn) return "most_relevant";
    else return "latest";
  }

  private replaceCurrentRoute(query: Record<string, string>): void {
    const currentQueryEntries = Object.entries(this.$router.currentRoute.query).toString();
    const newQueryEntries = Object.entries(query).toString();

    if (newQueryEntries !== currentQueryEntries) {
      this.$router.replace({ query: query }).catch(HandleRouterError);
      this.trackNewscenterActions(this.tabs.options.findIndex((obj) => obj.value === this.tabs.current));
    }
  }

  private async fetchArticles(): Promise<void> {
    let params: Indexable = { page: this.page };
    if (this.tabs.current === this.TABS.LATEST && this.filterByTags) params = { ...params, filter_by_tags: "1" };
    if (this.tabs.current === this.TABS.MOST_READ) params = { ...params, within: this.filterByWithin.current };

    try {
      if (this.page === 1) this.loader.start();
      else this.loadingBtnState = true;
      const response = await this.fetchArticlesForType(params);
      this.meta = response.data.meta;
      this.articles = [...this.articles, ...response.data.articles];
    } catch (errorMessage) {
      if (!CancelToken.isCancel(errorMessage)) {
        this.error = true;
        CreateToast.error(errorMessage as string);
      }
    } finally {
      this.cancelToken = null;
      this.loadingBtnState = false;
      this.loader.stop();
    }
  }

  private async fetchArticlesForType(params = {}): Promise<AxiosResponse<NewsCenterApi.AllResponsePayload>> {
    if (this.cancelToken) {
      this.cancelToken.cancel("Operation canceled by the user.");
    }
    this.cancelToken = CancelToken.CancelToken.source();
    switch (this.tabs.current) {
      case this.TABS.LATEST:
        return apiService.news_center.latest(this.cancelToken, params);
      case this.TABS.MOST_RELEVANT:
        return apiService.news_center.mostRelevant(this.cancelToken, params);
      default:
        return apiService.news_center.mostRead(this.cancelToken, params);
    }
  }

  private resetAndFetchAgain(): void {
    this.page = 1;
    this.articles = [];
    this.fetchArticles();
  }

  private trackNewscenterActions(targetIdentifier: string | boolean | number): void {
    ciTrackingService.trackView("Newscenter", targetIdentifier.toString());
  }

  private trackUserInterestClick(targetName: string): void {
    ciTrackingService.trackClick(targetName, "unused", "Newscenter");
  }
}
